import { useIssueInvoice } from "../../api/boq";
import { useOrderMove } from "../../api/purchaseOrders";
import { useRaiseBoq, type QuotationDetail } from "../../api/quotations";
import { nextStep, type StepKey } from "./pipeline";

interface Props {
  detail: QuotationDetail;
  /** Only the office moves a job on; anyone else reads the sentence. */
  canAct: boolean;
  onStep: (step: StepKey, options?: { read?: boolean }) => void;
}

/** One sentence under the job's header saying what happens next and who has
 * it, with the one button that does it (Fix #11). The button either performs
 * the move itself -- issue the order, raise the bill, issue the invoice -- or
 * opens the document where the move is made; it is never a second copy of a
 * form that lives on that document. */
export default function NextStepBanner({ detail, canAct, onStep }: Props) {
  const next = nextStep(detail);
  const raise = useRaiseBoq(detail.id);
  const issue = useOrderMove(detail.supplier_order?.id ?? 0);
  const invoice = useIssueInvoice(detail.boq?.id ?? 0);
  const busy = raise.isPending || issue.isPending || invoice.isPending;
  const error = raise.error ?? issue.error ?? invoice.error;
  const action = canAct ? next.action : null;

  function perform() {
    if (action === null) {
      return;
    }
    switch (action.kind) {
      case "goto":
        onStep(action.step, { read: action.read });
        return;
      case "raise-boq":
        raise.mutate(undefined, { onSuccess: () => onStep("boq") });
        return;
      case "issue-order":
        issue.mutate("issue", { onSuccess: () => onStep("po") });
        return;
      case "issue-invoice":
        invoice.mutate(undefined, { onSuccess: () => onStep("invoice") });
        return;
    }
  }

  return (
    <section className={next.done ? "cm-next done" : "cm-next"} aria-label={next.done ? "Done" : "Next step"}>
      <div className="cm-next-text">
        <span className="cm-next-label">{next.done ? "Done" : "Next step"}</span>
        <span data-testid="next-step-text">{next.text}</span>
        {error ? (
          <span className="cm-next-error" role="alert">
            {error.message}
          </span>
        ) : null}
      </div>
      {action !== null ? (
        <button type="button" className="btn btn-primary" disabled={busy} onClick={perform} data-testid="next-step-action">
          {busy ? "Working…" : action.label}
        </button>
      ) : null}
    </section>
  );
}
